// @flow

import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import Box from 'sail/Box';
import Switch from 'sail/Switch';
import {Text} from 'sail/Text';
import FlagIcon from 'sail/FlagIcon';

import {
  OnlyRenderOnClient,
  Table,
  TableHeaderCell,
  TableCell,
} from 'docs/components';
import {usePrefs, useLocation} from 'docs/data';
import type {TerminalContext} from 'docs/types';
import {
  countrySorter,
  countryNameMessages,
} from 'docs/components/contrib/Terminal/countries';
import {
  TerminalBetaFootnote,
  TerminalBetaFootnoteRef,
} from 'docs/components/contrib/Terminal/terminalBetaFootnote';
import {getReaderImageLink} from './reader-images';

import './TerminalCompatibilityMatrix.scss';

const messages = defineMessages({
  country: {
    id: 'docsShell.TerminalCompatibilityMatrix.country',
    description: 'Table header. Country',
    defaultMessage: 'Country',
  },
  countries: {
    id: 'docsShell.TerminalCompatibilityMatrix.countries',
    description: 'Table header. Countries',
    defaultMessage: 'Countries',
  },
  ios: {
    id: 'docsShell.TerminalCompatibilityMatrix.formatPlatform.ios',
    description: 'Platform label. iOS',
    defaultMessage: 'iOS',
  },
  js: {
    id: 'docsShell.TerminalCompatibilityMatrix.formatPlatform.js',
    description: 'Platform label. JavaScript',
    defaultMessage: 'JavaScript',
  },
  android: {
    id: 'docsShell.TerminalCompatibilityMatrix.formatPlatform.android',
    description: 'Platform label. Android',
    defaultMessage: 'Android',
  },
  server_driven: {
    id: 'docsShell.TerminalCompatibilityMatrix.formatPlatform.server.driven',
    description: 'Platform label. Server-Driven',
    defaultMessage: 'Server-Driven',
  },
  p400: {
    id: 'docsShell.TerminalCompatibilityMatrix.formatReader.p400',
    description: 'Terminal reader label. Verifone P400',
    defaultMessage: 'P400',
  },
  chipper: {
    id: 'docsShell.TerminalCompatibilityMatrix.formatReader.chipper',
    description: 'Terminal reader label. BBPOS Chipper 2X',
    defaultMessage: 'Chipper 2X',
  },
  stripe_m2: {
    id: 'docsShell.TerminalCompatibilityMatrix.formatReader.stripe.m2',
    description: 'Terminal reader label. Stripe Reader M2',
    defaultMessage: 'Stripe Reader M2',
  },
  wisepad3: {
    id: 'docsShell.TerminalCompatibilityMatrix.formatReader.wisepad3',
    description: 'Terminal reader label. BBPOS WisePad 3',
    defaultMessage: 'WisePad 3',
  },
  wisepos_e: {
    id: 'docsShell.TerminalCompatibilityMatrix.formatReader.wisepos.e',
    description: 'Terminal reader label. BBPOS WisePOS E',
    defaultMessage: 'WisePOS E',
  },
});

// Create some type aliases for the data from $terminal.compatibility_data
type CompatibilityData = $PropertyType<TerminalContext, 'compatibility_data'>;
type CountryDetails = $Values<CompatibilityData>;
type IntegrationShapeSet = $PropertyType<CountryDetails, 'shapes'>;
type IntegrationShape = $ElementType<IntegrationShapeSet, 0>;
type SerializedShapeSet = string;

const formatCountry = (countryCode: string) => {
  return <FormattedMessage {...(countryNameMessages[countryCode] ?? '')} />;
};

const formatPlatform = (platform: string) => {
  return <FormattedMessage {...messages[platform]} />;
};

const formatReader = (reader: string) => {
  return <FormattedMessage {...messages[reader]} />;
};

const getReaderCategory = (reader: string): 'Smart' | 'Bluetooth' => {
  return {
    p400: 'Smart',
    chipper: 'Bluetooth',
    wisepad3: 'Bluetooth',
    wisepos_e: 'Smart',
    stripe_m2: 'Bluetooth',
  }[reader];
};

const readerOrdering: Array<string> = [
  'stripe_m2',
  'chipper',
  'wisepad3',
  'wisepos_e',
];

// Compare shapes by platform, then reader, then release_scope returning a .sort() compatible numerical value
const shapeCompare = (a: IntegrationShape, b: IntegrationShape): number => {
  return (
    [
      a.platform.localeCompare(b.platform),
      readerOrdering
        .indexOf(a.reader)
        .toString()
        .localeCompare(readerOrdering.indexOf(b.reader).toString(), undefined, {
          numeric: true,
        }),
      a.release_scope.localeCompare(b.release_scope),
    ].find((comparisonResult) => comparisonResult !== 0) ?? 0
  );
};

// Check if two integration shape lists contain the exact same shapes
const doIntegrationShapeSetsMatch = (
  a: Array<IntegrationShape>,
  b: Array<IntegrationShape>,
): boolean => {
  return (
    a.every((aElement) =>
      b.find((bElement) => shapeCompare(aElement, bElement) === 0),
    ) &&
    b.every((bElement) =>
      a.find((aElement) => shapeCompare(bElement, aElement) === 0),
    )
  );
};

// Converts the CompatibilityData data structure into a mapping from integration shape set to list of countries
// e.g. {
//    [{platform: 'ios', reader: 'wisepad3', release_scope: 'beta'}] => ['AU', 'NZ', 'FR', 'DE', 'NL']
//    ...
// }
// This resulting data structure can then be iterated through to render a table where each row corresponds to a
// set of integration shapes and list of countries that have that set of shapes
const createShapesToCountriesMap = (
  compatibilityData: CompatibilityData,
): Map<Array<IntegrationShape>, Array<string>> => {
  // 1. Create Set with all integration shape sets (Array<IntegrationShape>)
  const shapeSet = new Set<SerializedShapeSet>();
  Object.keys(compatibilityData).forEach((country) => {
    const shapesForCountry = compatibilityData[country].shapes
      .slice() // slice to get a new array
      .sort(shapeCompare);
    shapeSet.add(JSON.stringify(shapesForCountry));
  });

  // 2. Create Map mapping each of them to empty array
  const shapeSetToCountryListMap = new Map<SerializedShapeSet, Set<string>>();
  shapeSet.forEach((shapeSetString) => {
    shapeSetToCountryListMap.set(shapeSetString, new Set<string>());
  });

  // 3. Go through each country's shape set and add country to relevant Map key
  Object.keys(compatibilityData).forEach((country) => {
    const arr = Array.from<[SerializedShapeSet, Set<string>]>(
      shapeSetToCountryListMap,
    );
    const candidate = arr.find(([key, value]) =>
      doIntegrationShapeSetsMatch(
        JSON.parse(key),
        compatibilityData[country].shapes,
      ),
    );

    if (candidate) {
      // Add the country to the value set
      shapeSetToCountryListMap.set(
        candidate[0],
        new Set([...candidate[1], country]),
      );
    }
  });

  const deserializedMap = new Map<Array<IntegrationShape>, Array<string>>();
  shapeSetToCountryListMap.forEach((listOfCountries, serializedShapeSet) => {
    deserializedMap.set(
      JSON.parse(serializedShapeSet),
      Array.from(listOfCountries),
    );
  });

  return deserializedMap;
};

const isCountryInBeta = (
  countryCompatibilityData: $Values<CompatibilityData> | null,
): boolean => {
  // There's an edge case where a user may set their selected country pref to an invite-only country
  // and then log out but the pref doesn't clear, causing compatibilityDataForCountry to be undefined
  // If this happens, we should assume the country is in beta
  return countryCompatibilityData?.in_beta ?? true;
};

export type TerminalCompatibilityMatrixProps = {|
  compatibilityData: CompatibilityData | string,
  onlyShowIntegrationCountry: boolean,
|};

export const TerminalCompatibilityMatrix = ({
  compatibilityData: compatibilityDataOfUnknownType,
  onlyShowIntegrationCountry = false,
  showCountryFilterToggle = false,
}: TerminalCompatibilityMatrixProps) => {
  const {getPref} = usePrefs();
  const {location} = useLocation();

  const [showAll, setShowAll] = React.useState(() => {
    const {expand} = location.query;
    return expand != null;
  });

  // In https://git.corp.stripe.com/stripe-internal/pay-server/pull/307173,
  // we changed the compatibility_data field of the terminal context from
  // a string that has to get JSON.parse()'d to a struct that docs_renderer automatically
  // converts to the proper object shape. While we transition though, we need to be able
  // to handle both because docs deploys just a bit earlier than site-srv so
  // site-srv would continue sending a string even though this code is expecting an object
  // TODO(bibek, 2020-04-15) Remove this conditional logic once site-srv has been deployed with the updated context field type
  const compatibilityData: CompatibilityData = React.useMemo(
    () =>
      typeof compatibilityDataOfUnknownType === 'string'
        ? JSON.parse(compatibilityDataOfUnknownType)
        : compatibilityDataOfUnknownType,
    [compatibilityDataOfUnknownType],
  );

  const selectedCountry = getPref<string>('integration-country');
  // TODO: use correct dependencies
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  const filteredCompatibilityData =
    !showAll &&
    onlyShowIntegrationCountry &&
    selectedCountry &&
    compatibilityData.hasOwnProperty(selectedCountry)
      ? {[selectedCountry]: compatibilityData[selectedCountry]}
      : compatibilityData;

  // Compute a memoized list of all platforms to be used later
  const platforms = React.useMemo(() => {
    const platformSet = new Set();
    Object.keys(filteredCompatibilityData).forEach((country) => {
      filteredCompatibilityData[country].shapes.forEach((shape) => {
        platformSet.add(shape.platform);
      });
    });
    return Array.from(platformSet).sort();
  }, [filteredCompatibilityData]);

  // Converts the filteredCompatibilityData data structure
  const shapesToCountriesMap = createShapesToCountriesMap(
    filteredCompatibilityData,
  );

  const numCountries = Array.from(shapesToCountriesMap.entries()).reduce(
    (acc, [_, countries]) => acc + countries.length,
    0,
  );

  const showingBetaCountries = React.useMemo(
    () =>
      Object.keys(filteredCompatibilityData).some((country) =>
        isCountryInBeta(filteredCompatibilityData[country]),
      ),
    [filteredCompatibilityData],
  );

  const showingBetaIntegrationShapes = React.useMemo(
    () =>
      Object.keys(filteredCompatibilityData).some((country) =>
        filteredCompatibilityData[country].shapes.some(
          (shape) => shape.release_scope === 'beta',
        ),
      ),
    [filteredCompatibilityData],
  );

  return (
    <Box className="TerminalCompatibilityMatrix">
      {/**
        Only render the Switch on client in order to prevent a bug when the `?expand` query param is provided:
        1. the server doesn't have access to query params
        2. the server doesn't see the `?expand` query param even though it's present client-side
        3. the server sets the showAll state variable to false
        4. when the client renders, the Switch doesn't properly update its UI to look selected
           - ideally, the Switch would update; this appears to be a bug with Switch not being SSR-friendly
        */}
      <OnlyRenderOnClient>
        {showCountryFilterToggle && (
          <div
            style={{
              marginBottom: -12,
              marginTop: 12,
            }}
          >
            <Switch
              id="showAllCountriesSwitch"
              onChange={() => {
                setShowAll(!showAll);
              }}
              value={showAll}
              size="small"
              label="Show all available countries in table"
            />
          </div>
        )}
      </OnlyRenderOnClient>
      <Table type="striped">
        <thead>
          <tr>
            <TableHeaderCell align="left">
              {numCountries === 1 ? (
                <FormattedMessage {...messages.country} />
              ) : (
                <FormattedMessage {...messages.countries} />
              )}
            </TableHeaderCell>
            {platforms.map((platform) => (
              <TableHeaderCell align="left" key={platform}>
                {formatPlatform(platform)}
              </TableHeaderCell>
            ))}
          </tr>
        </thead>

        <tbody>
          {Array.from(shapesToCountriesMap.entries()).map(
            ([shapes, countries]) => {
              return (
                <tr
                  key={countries.join('-')}
                  className={
                    showAll && countries.includes(selectedCountry)
                      ? 'SelectedCountry'
                      : ''
                  }
                >
                  <TableCell>
                    {countries.sort(countrySorter).map((country) => (
                      <Box key={country} flex={{alignItems: 'center'}}>
                        <Box margin={{right: 4}}>
                          {/* $FlowExpectedError FlagIcon is very strongly typed but `country` is typed as any string; it's fine though since it'll only be proper countries */}
                          <FlagIcon countryCode={country} />
                        </Box>
                        <Box flex={{alignItems: 'center'}}>
                          {formatCountry(country)}
                          {isCountryInBeta(compatibilityData[country]) && (
                            <TerminalBetaFootnoteRef betaCountry />
                          )}
                        </Box>
                      </Box>
                    ))}
                  </TableCell>
                  {platforms.map((platform) => (
                    <TableCell key={platform}>
                      {shapes
                        .filter((shape) => shape.platform === platform)
                        .sort(shapeCompare)
                        .map(({reader, release_scope}, index) => (
                          <Box
                            key={reader}
                            flex={{alignItems: 'flex-start'}}
                            // Slightly hacky way of achieving margin only between readers
                            margin={{top: index !== 0 ? 16 : 0}}
                          >
                            <Box>
                              <Box style={{width: '50px', height: '50px'}}>
                                <img
                                  width="100%"
                                  height="100%"
                                  style={{
                                    objectFit: 'contain',
                                    objectPosition: 'center',
                                  }}
                                  src={getReaderImageLink(reader)}
                                  alt=""
                                />
                              </Box>
                              <Box flex={{alignItems: 'center'}}>
                                {formatReader(reader)}
                                {release_scope === 'beta' && (
                                  <TerminalBetaFootnoteRef
                                    betaIntegrationShape
                                  />
                                )}
                              </Box>
                              <Text color="gray" size={12}>
                                {getReaderCategory(reader)}
                              </Text>
                            </Box>
                          </Box>
                        ))}
                    </TableCell>
                  ))}
                </tr>
              );
            },
          )}
        </tbody>
      </Table>

      <TerminalBetaFootnote
        betaCountry={showingBetaCountries}
        betaIntegrationShape={showingBetaIntegrationShapes}
      />
    </Box>
  );
};
